package com.iptvplayer.shared.data.repository

import com.iptvplayer.shared.data.remote.api.FavoriteApi
import com.iptvplayer.shared.data.remote.dto.AddFavoriteRequestDto
import com.iptvplayer.shared.domain.model.ContentType
import com.iptvplayer.shared.domain.model.Favorite
import com.iptvplayer.shared.domain.repository.FavoriteRepository
import com.iptvplayer.shared.util.Result
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow

class FavoriteRepositoryImpl(
    private val favoriteApi: FavoriteApi
) : FavoriteRepository {

    private val favoritesFlow = MutableStateFlow<List<Favorite>>(emptyList())

    override fun getFavorites(): Flow<List<Favorite>> = favoritesFlow

    override suspend fun addFavorite(
        contentId: String,
        contentType: ContentType,
        contentName: String,
        contentLogoUrl: String?,
        streamUrl: String
    ): Result<Favorite> = try {
        val dto = favoriteApi.addFavorite(
            AddFavoriteRequestDto(
                contentId = contentId,
                contentType = contentType.name,
                contentName = contentName,
                contentLogoUrl = contentLogoUrl,
                streamUrl = streamUrl
            )
        )
        val favorite = Favorite(
            id = dto.id,
            userId = dto.userId,
            contentId = dto.contentId,
            contentType = parseContentType(dto.contentType),
            contentName = dto.contentName,
            contentLogoUrl = dto.contentLogoUrl,
            streamUrl = dto.streamUrl,
            addedAt = dto.addedAt
        )
        val current = favoritesFlow.value.toMutableList()
        current.removeAll { it.contentId == contentId }
        current.add(favorite)
        favoritesFlow.value = current
        Result.Success(favorite)
    } catch (e: Exception) {
        Result.Error("Failed to add favorite: ${e.message}", e)
    }

    override suspend fun removeFavorite(contentId: String): Result<Unit> = try {
        favoriteApi.removeFavorite(contentId)
        favoritesFlow.value = favoritesFlow.value.filter { it.contentId != contentId }
        Result.Success(Unit)
    } catch (e: Exception) {
        Result.Error("Failed to remove favorite: ${e.message}", e)
    }

    override suspend fun isFavorite(contentId: String): Boolean =
        favoritesFlow.value.any { it.contentId == contentId }

    override suspend fun syncFavorites(): Result<Unit> = try {
        val dtos = favoriteApi.getFavorites()
        favoritesFlow.value = dtos.map { dto ->
            Favorite(
                id = dto.id,
                userId = dto.userId,
                contentId = dto.contentId,
                contentType = parseContentType(dto.contentType),
                contentName = dto.contentName,
                contentLogoUrl = dto.contentLogoUrl,
                streamUrl = dto.streamUrl,
                addedAt = dto.addedAt
            )
        }
        Result.Success(Unit)
    } catch (e: Exception) {
        Result.Error("Failed to sync favorites: ${e.message}", e)
    }

    private fun parseContentType(type: String): ContentType = try {
        ContentType.valueOf(type)
    } catch (_: Exception) {
        ContentType.CHANNEL
    }
}
